import { FaNetworkWired, FaDesktop, FaLaptop, FaServer, FaVideo } from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook to detect if section is on-screen
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function AboutServices() {
  // Fixed background image (you can update the image path as needed)
  const bgImageUrl = "/img/img6.jpeg";
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Detect if this section is visible on screen
  const isVisible = useOnScreen(sectionRef, "-70px"); // trigger a bit before fully visible

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  const services = [
    {
      icon: <FaNetworkWired />,
      title: "Network Infrastructure",
      desc: "Complete network setup, monitoring and maintenance services ensuring reliable connectivity and stable IT infrastructure."
    },
    {
      icon: <FaDesktop />,
      title: "Computer Systems Support",
      desc: "Professional maintenance and troubleshooting services that keep business desktops performing efficiently."
    },
    {
      icon: <FaLaptop />,
      title: "Laptop Lifecycle Management",
      desc: "From hardware diagnostics to software optimization, ensuring portable systems stay reliable and productive."
    },
    {
      icon: <FaServer />,
      title: "Server Management",
      desc: "Secure and dependable server management including monitoring, upgrades and data protection solutions."
    },
    {
      icon: <FaVideo />,
      title: "Security & Surveillance",
      desc: "Advanced CCTV system deployment and maintenance helping organizations strengthen physical security."
    }
  ];

  // Animation style helper: move more slowly upward (increase duration)
  const animStyle = (delay = 0, y = 36, duration = 1800) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0)" : `translateY(${y}px)`,
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  return (
    <section
      ref={sectionRef}
      className="py-24 font-PTSerif-Regular relative"
      style={{
        backgroundImage: `url('${bgImageUrl}')`,
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-gray-100/80 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 font-PTSerif-Regular">
        <div
          className="text-center mb-16 font-PTSerif-Regular"
          style={animStyle(0, 30, 1800)}
        >
          <h2 className="text-4xl font-bold text-gray-900 font-PTSerif-Regular">
            Our Expertise
          </h2>
          <div className="w-16 h-[3px] bg-blue-500 mx-auto mt-4"></div>
          <p className="text-gray-600 mt-6 max-w-2xl mx-auto font-PTSerif-Regular">
            Our engineers deliver comprehensive IT solutions designed to
            improve system performance, minimize downtime, and protect
            business operations.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 font-PTSerif-Regular">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-lg shadow hover:shadow-lg transition font-PTSerif-Regular"
              style={animStyle(150 + index * 80, 40, 1800)}
            >
              <div className="text-blue-600 text-3xl mb-4 font-PTSerif-Regular">
                {service.icon}
              </div>
              <h3 className="font-semibold text-lg mb-3 font-PTSerif-Regular">
                {service.title}
              </h3>
              <p className="text-gray-600 text-sm font-PTSerif-Regular">
                {service.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}